<template>
  <div class="p-4 sm:p-6 md:p-8">
    <div class="max-w-7xl mx-auto">
      <div v-if="loading" class="text-center py-20">
        <p class="text-gray-600">Loading issue details...</p>
      </div>
      <div v-else-if="error" class="text-red-500 text-center py-20">{{ error }}</div>
      
      <div v-else-if="report" class="space-y-8">
        <!-- Header -->
        <div class="flex items-center justify-between mb-6 border-b pb-4">
          <div>
            <h1 class="text-2xl sm:text-3xl font-bold text-gray-800">Issue Report Details</h1>
            <p class="text-sm text-gray-500 mt-1">
              Reported by {{ report.reporterName }} ({{ report.clientName }}) on {{ formatDate(report.createdAt) }}
            </p>
          </div>
          <button @click="goBack" class="text-sm text-brand-primary hover:text-brand-accent hover:underline">
            &larr; Back to List
          </button>
        </div>

        <!-- Report Details Card -->
        <div class="bg-white p-6 rounded-lg shadow-lg">
          <h2 class="text-xl font-semibold text-brand-primary mb-4">Initial Report</h2>
          <p class="text-gray-700 whitespace-pre-wrap">{{ report.initialReport }}</p>
          
          <div v-if="report.imageUrl" class="mt-6">
            <h3 class="text-lg font-medium text-gray-800 mb-2">Attached Screenshot</h3>
            <a :href="report.imageUrl" target="_blank" rel="noopener noreferrer" class="block border rounded-lg overflow-hidden hover:shadow-lg transition-shadow w-full max-w-lg">
              <img :src="report.imageUrl" alt="Issue Screenshot" class="w-full h-auto object-contain">
            </a>
          </div>
        </div>

        <!-- Replies Section -->
        <div class="bg-white p-6 rounded-lg shadow-lg">
          <h2 class="text-xl font-semibold text-brand-primary mb-4">Conversation</h2>
          <div class="space-y-4">
            <div v-if="report.replies && report.replies.length > 0">
              <div v-for="(reply, index) in report.replies" :key="index" class="p-4 rounded-md"
                   :class="reply.authorId === auth.currentUser.uid ? 'bg-blue-50' : 'bg-gray-50'">
                <p class="text-sm text-gray-800 whitespace-pre-wrap">{{ reply.text }}</p>
                <p class="text-xs text-gray-500 mt-2 text-right">
                  - {{ reply.authorName }} on {{ formatDate(reply.timestamp) }}
                </p>
              </div>
            </div>
            <p v-else class="text-sm text-gray-500">No replies yet.</p>
            
            <!-- Add Reply Form -->
            <form @submit.prevent="handleSubmitReply" class="pt-6 border-t">
              <label for="newReply" class="block text-sm font-medium text-gray-700 mb-1">Add Your Reply</label>
              <textarea id="newReply" v-model="newReplyText" rows="4" required class="form-input"></textarea>
              <div class="mt-3 flex justify-end">
                <button type="submit" :disabled="isSubmittingReply" class="form-action-btn-primary">
                   {{ isSubmittingReply ? 'Submitting...' : 'Submit Reply' }}
                </button>
              </div>
            </form>
          </div>
        </div>

        <!-- Super Admin Section -->
        <div v-if="isSuperAdmin" class="bg-white p-6 rounded-lg shadow-lg">
          <h2 class="text-xl font-semibold text-brand-primary mb-4">Admin Actions</h2>
          <div class="flex items-center space-x-3">
            <label for="status-select" class="text-sm font-medium text-gray-700 whitespace-nowrap">Update Status:</label>
            <select v-model="selectedStatus" id="status-select" class="form-input w-48">
              <option value="new">New</option>
              <option value="in-progress">In Progress</option>
              <option value="complete">Complete</option>
            </select>
            <!-- DEFINITIVE FIX: Added 'whitespace-nowrap' to prevent the button text from wrapping -->
            <button @click="handleStatusUpdate" :disabled="isUpdatingStatus" class="form-action-btn-primary whitespace-nowrap">
              {{ isUpdatingStatus ? 'Saving...' : 'Save Status' }}
            </button>
          </div>
           <p v-if="statusUpdateMessage" class="mt-3 text-sm text-green-600">{{ statusUpdateMessage }}</p>
        </div>

      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { db, auth, firebaseApp } from '@/firebaseConfig';
import { doc, onSnapshot } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useAuth } from '@/composables/useAuth';

const route = useRoute();
const router = useRouter();
const functions = getFunctions(firebaseApp, 'europe-west2');
const { claims } = useAuth();

const reportId = route.params.reportId;
const report = ref(null);
const loading = ref(true);
const error = ref(null);
const newReplyText = ref('');
const isSubmittingReply = ref(false);
const isUpdatingStatus = ref(false);
const selectedStatus = ref('');
const statusUpdateMessage = ref('');

let unsubscribe = null;

const isSuperAdmin = computed(() => claims.value?.role === 'super-admin');

const goBack = () => router.go(-1);

const formatDate = (timestamp) => {
  if (!timestamp || !timestamp.seconds) return 'N/A';
  return new Date(timestamp.seconds * 1000).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
  });
};

onMounted(() => {
  const docRef = doc(db, 'issueReports', reportId);
  
  unsubscribe = onSnapshot(docRef, (docSnap) => {
    if (docSnap.exists()) {
      report.value = { id: docSnap.id, ...docSnap.data() };
      selectedStatus.value = report.value.status;
    } else {
      error.value = "Issue report not found.";
    }
    loading.value = false;
  }, (err) => {
    console.error("Error fetching issue report:", err);
    error.value = "Failed to load issue report details.";
    loading.value = false;
  });
});

onUnmounted(() => {
  if (unsubscribe) {
    unsubscribe();
  }
});

const handleSubmitReply = async () => {
  if (!newReplyText.value.trim()) return;
  isSubmittingReply.value = true;
  try {
    const addReplyFunction = httpsCallable(functions, 'addIssueReportReply');
    await addReplyFunction({ reportId: report.value.id, text: newReplyText.value });
    newReplyText.value = '';
  } catch (err) {
    console.error("Error submitting reply:", err);
    alert(`Failed to submit reply: ${err.message}`);
  } finally {
    isSubmittingReply.value = false;
  }
};

const handleStatusUpdate = async () => {
  isUpdatingStatus.value = true;
  statusUpdateMessage.value = '';
  try {
    const updateStatusFunction = httpsCallable(functions, 'updateIssueReportStatus');
    await updateStatusFunction({ reportId: report.value.id, status: selectedStatus.value });
    statusUpdateMessage.value = 'Status updated successfully!';
    setTimeout(() => statusUpdateMessage.value = '', 3000);
  } catch (err) {
    console.error("Error updating status:", err);
    alert(`Failed to update status: ${err.message}`);
  } finally {
    isUpdatingStatus.value = false;
  }
};
</script>

<style scoped>
.form-input { @apply block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-brand-primary focus:border-brand-primary sm:text-sm; }
.form-action-btn-primary { @apply bg-brand-primary text-white py-2 px-4 rounded-md hover:bg-brand-accent transition-colors duration-200 text-sm font-medium shadow-sm disabled:opacity-50; }
</style>